import { useState } from 'react';
import { Alert, Box, Button, Divider, Stack, TextField, Typography } from '@mui/material';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import type { FeedbackVerdict, HouseholdDetail } from '@hv/shared';
import { useClearOverride, useFeedback, useRescore, useSetOverride } from '../api/hooks';
import { useAuth } from '../components/AuthContext';
import { ScoreRing, SignalChips } from '../components/badges';
import { useNotify } from '../components/Notify';
import { BRAND } from '../theme';
import { Section } from './Section';

const fmtDate = (iso: string) => iso.slice(0, 10);

export function AssessmentSection({ h }: { h: HouseholdDetail }) {
  const { can } = useAuth();
  const { notify, notifyError } = useNotify();
  const setOverride = useSetOverride();
  const clearOverride = useClearOverride();
  const feedback = useFeedback();
  const rescore = useRescore();
  const [score, setScore] = useState(h.override ? String(h.override.overrideScore) : '');
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const a = h.assessment;
  const scoreNum = Number(score);
  const scoreValid = score !== '' && Number.isInteger(scoreNum) && scoreNum >= 0 && scoreNum <= 100;

  const submitOverride = () => {
    setOverride.mutate(
      { id: h.id, score: scoreNum, reason: reason.trim() },
      {
        onSuccess: () => {
          setReason('');
          notify('success', 'Override saved');
        },
        onError: (err) => notifyError(err, 'Saving override failed'),
      },
    );
  };

  const submitFeedback = (verdict: FeedbackVerdict) => {
    if (!a) return;
    feedback.mutate(
      { assessmentId: a.id, verdict, comment: comment.trim() || null },
      {
        onSuccess: () => {
          setComment('');
          notify('success', 'Feedback recorded');
        },
        onError: (err) => notifyError(err, 'Saving feedback failed'),
      },
    );
  };

  return (
    <Section title="High Visibility Assessment">
      <Stack direction="row" gap={2} alignItems="flex-start">
        <ScoreRing score={h.effectiveScore} band={h.band} />
        <Box sx={{ minWidth: 0 }}>
          <Box mb={1}>
            <SignalChips signals={h.signals} />
          </Box>
          <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
            {a?.rationale ?? (h.assessmentStatus === 'pending' ? 'Scoring is queued.' : 'This household has not been scored yet.')}
          </Typography>
          {a && (
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              {a.model} · prompt {a.promptVersion} · thresholds {a.thresholdVersion} · scored {fmtDate(a.scoredAt)}
              {a.confidence !== null ? ` · confidence ${Math.round(a.confidence * 100)}%` : ''}
            </Typography>
          )}
        </Box>
      </Stack>
      {a?.failureReason && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          Latest scoring attempt failed: {a.failureReason}
        </Alert>
      )}
      {can('scores.rescore') && (
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          sx={{ mt: 1 }}
          disabled={rescore.isPending}
          onClick={() =>
            rescore.mutate(h.id, {
              onSuccess: (r) =>
                notify(
                  r.status === 'failed' ? 'warning' : 'success',
                  r.status === 'succeeded'
                    ? `Re-scored: ${r.score}`
                    : r.status === 'unchanged'
                      ? 'Inputs unchanged since last score; nothing to do'
                      : r.status === 'no_input'
                        ? 'No narrative or recommendation text to score'
                        : `Scoring failed: ${r.reason ?? 'unknown reason'}`,
                ),
              onError: (err) => notifyError(err, 'Re-scoring failed'),
            })
          }
        >
          {rescore.isPending ? 'Scoring…' : 'Re-score now'}
        </Button>
      )}

      <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f8fafc', border: `1px solid ${BRAND.line}`, borderRadius: 2 }} data-testid="override-box">
        <Typography variant="body2" data-testid="override-status">
          {h.override ? (
            <>
              <strong>Overridden</strong> · effective score <strong>{h.override.overrideScore}</strong> · AI score was <strong>{h.aiScore ?? '—'}</strong> · by{' '}
              {h.override.byUserName} on {fmtDate(h.override.at)} — “{h.override.reason}”
            </>
          ) : (
            <>
              <strong>AI score</strong> {h.aiScore ?? '—'} · no override set
            </>
          )}
        </Typography>
        {can('scores.override') && (
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} mt={1.5} alignItems={{ sm: 'flex-start' }}>
            <TextField
              size="small"
              label="Override score"
              type="number"
              value={score}
              onChange={(e) => setScore(e.target.value)}
              slotProps={{ htmlInput: { min: 0, max: 100, step: 1 } }}
              error={score !== '' && !scoreValid}
              sx={{ width: 140 }}
            />
            <TextField size="small" label="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} sx={{ flex: 1 }} />
            <Button variant="contained" aria-label="Set override" disabled={!scoreValid || !reason.trim() || setOverride.isPending || h.aiScore === null} onClick={submitOverride}>
              Set
            </Button>
            <Button
              disabled={!h.override || clearOverride.isPending}
              onClick={() =>
                clearOverride.mutate(
                  { id: h.id, reason: reason.trim() || undefined },
                  {
                    onSuccess: () => {
                      setScore('');
                      notify('success', 'Reset to AI score');
                    },
                    onError: (err) => notifyError(err, 'Resetting override failed'),
                  },
                )
              }
            >
              Reset to AI
            </Button>
          </Stack>
        )}
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          The AI score is always retained; lists rank by the effective score.
        </Typography>
      </Box>

      {a && a.status === 'succeeded' && can('scores.feedback') && (
        <Box mt={2}>
          <Typography variant="body2" fontWeight={600} mb={1}>
            Is this assessment right?
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
            <TextField size="small" label="Comment (optional)" value={comment} onChange={(e) => setComment(e.target.value)} sx={{ flex: 1 }} />
            <Button variant="outlined" color="success" startIcon={<ThumbUpOffAltIcon />} disabled={feedback.isPending} onClick={() => submitFeedback('confirm')}>
              Confirm
            </Button>
            <Button variant="outlined" color="error" startIcon={<ThumbDownOffAltIcon />} disabled={feedback.isPending} onClick={() => submitFeedback('reject')}>
              Reject
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            Feedback is reviewed by staff to tune the prompt and thresholds. It never trains a model.
          </Typography>
        </Box>
      )}
      {h.feedback.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Stack gap={0.5} component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
            {h.feedback.map((f) => (
              <Typography component="li" variant="caption" key={f.id}>
                <strong>{f.verdict === 'confirm' ? 'Confirmed' : 'Rejected'}</strong> by {f.byUserName} on {fmtDate(f.at)}
                {f.comment ? ` — ${f.comment}` : ''}
              </Typography>
            ))}
          </Stack>
        </>
      )}
    </Section>
  );
}
